import type { ProStudioContext } from '../xstate/prostudio.xstate.types';
import { resolveSolutionId } from './resolve-bot-solution-id';

/**
 * The id of the solution the route has open — the key for the app-wide publish
 * subscriptions (`usePublishStatusWatcherForCoworkers`, `useSolutionDeployments`).
 *
 * Resolved from the route (`resolveSolutionId`: `botName` against the solution list) before
 * `selectedBot`, because `selectedBot` is cleared and rebuilt on every publish
 * (`publishingBotFlow` onDone, `reloadCurrentBotData`). Keyed on it, both subscriptions tore
 * down mid-publish and the watcher forgot the attempt it was showing — a publish that failed
 * fast was never announced to the person who started it (PSTUDIO-4333).
 *
 * `selectedBot` is the fallback only while the list does not have the route's solution yet,
 * and only when it does not name a DIFFERENT solution: a browser POP moves `botName` without
 * touching `selectedBot` (PSTUDIO-4121), and a key on the solution the user came from would
 * leak its lock and toast onto the one now open. Not `isSelectedBotStale`, which also rejects
 * a copy with no `botName` — right for deciding whether to re-fetch, but here it would drop
 * the only key there is.
 */
export const selectOpenSolutionId = (
  ctx: Pick<ProStudioContext, 'botName' | 'solutions' | 'selectedBot'>
): string | null => {
  const routeSolutionId = resolveSolutionId(ctx);
  if (routeSolutionId) return routeSolutionId;

  const selectedBotName = ctx.selectedBot?.botName?.trim();
  if (selectedBotName && selectedBotName !== ctx.botName?.trim()) return null;

  return ctx.selectedBot?.uniqueId || null;
};
